import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import {
  getDocumentsByDossier,
  updateDocumentStatus,
  requestComplementaryDocument,
  updateDocumentCompliance,
  getDocumentSignedUrl,
  DOCUMENT_COMPLIANCE_CHECKLISTS,
} from '../../../services/notairePortalService';
import {
  getAnnotationsByDocument,
  addAnnotation,
  resolveAnnotation,
  deleteAnnotation,
  addTimelineEvent,
} from '../../../services/notaryWorkspaceService';
import DocumentUploadModal from './DocumentUploadModal';

const DOC_STATUS_CONFIG = {
  en_attente: { label: 'En attente', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'Clock' },
  valide: { label: 'Validé', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: 'CheckCircle2' },
  rejete: { label: 'Rejeté', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'XCircle' },
  complementaire_demande: { label: 'Complémentaire', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: 'AlertCircle' },
};

const DOC_TYPE_ICONS = {
  titre_propriete: { icon: 'FileText', color: 'text-amber-600', bg: 'bg-amber-50' },
  identite: { icon: 'CreditCard', color: 'text-blue-600', bg: 'bg-blue-50' },
  plan: { icon: 'Map', color: 'text-green-600', bg: 'bg-green-50' },
  acte: { icon: 'ScrollText', color: 'text-purple-600', bg: 'bg-purple-50' },
  fiscal: { icon: 'Receipt', color: 'text-rose-600', bg: 'bg-rose-50' },
  autre: { icon: 'File', color: 'text-slate-600', bg: 'bg-slate-50' },
};

const ANNOTATION_TYPE_CONFIG = {
  comment: { label: 'Commentaire', icon: 'MessageSquare', color: 'text-blue-600', bg: 'bg-blue-50' },
  correction: { label: 'Correction', icon: 'Edit3', color: 'text-amber-600', bg: 'bg-amber-50' },
  approval_note: { label: 'Approbation', icon: 'CheckCircle', color: 'text-green-600', bg: 'bg-green-50' },
  flag: { label: 'Signalement', icon: 'Flag', color: 'text-red-600', bg: 'bg-red-50' },
};

const UPLOADER_ROLE_LABELS = {
  acheteur: 'Acheteur',
  vendeur: 'Vendeur',
  notaire: 'Notaire',
  partie: 'Partie tierce',
};

const ComplianceBar = ({ score }) => {
  const color = score >= 80 ? 'bg-green-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-400';
  const textColor = score >= 80 ? 'text-green-600' : score >= 50 ? 'text-amber-600' : 'text-red-500';
  return (
    <div className="flex items-center space-x-2 mt-1.5">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${score || 0}%` }} />
      </div>
      <span className={`text-[10px] font-system font-body-semibold ${textColor} w-8 text-right`}>{score || 0}%</span>
    </div>
  );
};

// Inline Annotations Panel — optimized for mobile
const AnnotationsPanel = ({ doc, notaireProfile, onAnnotationAdded }) => {
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAnnotation, setNewAnnotation] = useState('');
  const [annotationType, setAnnotationType] = useState('comment');
  const [adding, setAdding] = useState(false);

  const loadAnnotations = async () => {
    setLoading(true);
    const { data } = await getAnnotationsByDocument(doc?.id);
    setAnnotations(data || []);
    setLoading(false);
  };

  useEffect(() => { loadAnnotations(); }, [doc?.id]);

  const handleAdd = async () => {
    if (!newAnnotation?.trim() || !notaireProfile?.id) return;
    setAdding(true);
    await addAnnotation(doc?.id, notaireProfile?.id, newAnnotation, annotationType);
    await addTimelineEvent(doc?.dossier_id || '', 'annotation_added', `Annotation ajoutée sur "${doc?.document_name}"`, newAnnotation, 'notaire');
    setNewAnnotation('');
    await loadAnnotations();
    onAnnotationAdded?.();
    setAdding(false);
  };

  const handleResolve = async (annotationId) => {
    await resolveAnnotation(annotationId);
    await loadAnnotations();
  };

  const handleDelete = async (annotationId) => {
    await deleteAnnotation(annotationId);
    await loadAnnotations();
  };

  return (
    <div className="border-t border-border/50 bg-slate-50/80 px-4 py-4 space-y-3">
      <p className="text-[11px] font-system font-body-semibold text-text-secondary uppercase tracking-wider flex items-center space-x-1.5">
        <Icon name="MessageSquare" size={12} />
        <span>Annotations notaire ({annotations?.length})</span>
      </p>

      {/* Annotation type selector — scrollable on mobile */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
        {Object.entries(ANNOTATION_TYPE_CONFIG)?.map(([key, conf]) => (
          <button key={key} onClick={() => setAnnotationType(key)}
            className={`flex-shrink-0 flex items-center space-x-1 px-3 py-2 rounded-full text-[11px] font-system font-body-semibold transition-all border touch-manipulation ${
              annotationType === key ? `${conf?.bg} ${conf?.color} border-current/30` : 'bg-white text-text-secondary border-border hover:border-slate-300 active:bg-slate-50'
            }`}>
            <Icon name={conf?.icon} size={11} />
            <span>{conf?.label}</span>
          </button>
        ))}
      </div>

      {/* Input row */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newAnnotation}
          onChange={e => setNewAnnotation(e?.target?.value)}
          onKeyDown={e => e?.key === 'Enter' && handleAdd()}
          placeholder="Ajouter une annotation..."
          className="flex-1 px-3 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent bg-white"
        />
        <button onClick={handleAdd} disabled={adding || !newAnnotation?.trim()}
          className="w-11 h-11 bg-brand-earth text-white rounded-xl font-system font-body-semibold hover:bg-brand-earth/90 active:bg-brand-earth/80 disabled:opacity-60 transition-colors flex-shrink-0 flex items-center justify-center touch-manipulation">
          {adding ? <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="Send" size={15} />}
        </button>
      </div>

      {/* Annotations list */}
      {loading ? (
        <div className="space-y-1.5">{[1, 2]?.map(i => <div key={i} className="h-10 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : annotations?.length === 0 ? (
        <p className="text-[11px] text-text-secondary font-system text-center py-2">Aucune annotation</p>
      ) : (
        <div className="space-y-2">
          {annotations?.map(ann => {
            const conf = ANNOTATION_TYPE_CONFIG?.[ann?.annotation_type] || ANNOTATION_TYPE_CONFIG?.comment;
            return (
              <div key={ann?.id} className={`flex items-start space-x-2 p-3 rounded-xl border ${ann?.is_resolved ? 'opacity-50 bg-white' : `${conf?.bg} border-current/20`}`}>
                <div className={`w-7 h-7 rounded-full ${conf?.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <Icon name={conf?.icon} size={12} className={conf?.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-ios-callout font-system ${ann?.is_resolved ? 'line-through text-text-secondary' : 'text-text-primary'}`}>{ann?.annotation_text}</p>
                  <p className="text-[10px] text-text-secondary font-system mt-0.5">{conf?.label} · {ann?.notary?.name || 'Notaire'}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {!ann?.is_resolved && (
                    <button onClick={() => handleResolve(ann?.id)} title="Résoudre"
                      className="w-8 h-8 rounded-full bg-green-100 hover:bg-green-200 active:bg-green-300 flex items-center justify-center transition-colors touch-manipulation">
                      <Icon name="Check" size={13} className="text-green-700" />
                    </button>
                  )}
                  <button onClick={() => handleDelete(ann?.id)} title="Supprimer"
                    className="w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 active:bg-red-200 flex items-center justify-center transition-colors touch-manipulation">
                    <Icon name="X" size={13} className="text-red-500" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DocumentVault = ({ dossierId, onDocumentUpdated, isNotaire = false, notaireProfile = null }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ document_name: '', document_type: '' });
  const [requesting, setRequesting] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [expandedAnnotations, setExpandedAnnotations] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchMode, setBatchMode] = useState(false);

  const fetchDocuments = async () => {
    if (!dossierId) return;
    setLoading(true);
    const { data } = await getDocumentsByDossier(dossierId);
    setDocuments(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchDocuments(); }, [dossierId]);

  const handleUpdateStatus = async (docId, status, notes = '') => {
    setUpdatingId(docId);
    await updateDocumentStatus(docId, status, notes);
    const doc = documents?.find(d => d?.id === docId);
    if (doc) {
      const eventType = status === 'valide' ? 'document_approved' : status === 'rejete' ? 'document_rejected' : 'status_change';
      const eventTitle = status === 'valide' ? `Document validé : ${doc?.document_name}` : status === 'rejete' ? `Document rejeté : ${doc?.document_name}` : `Statut mis à jour : ${doc?.document_name}`;
      await addTimelineEvent(dossierId, eventType, eventTitle, notes || '', 'notaire');
    }
    await fetchDocuments();
    onDocumentUpdated?.();
    setUpdatingId(null);
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    await handleUpdateStatus(rejectModal, 'rejete', rejectReason);
    setRejectModal(null);
    setRejectReason('');
  };

  const handleRequestDoc = async (e) => {
    e?.preventDefault();
    if (!requestForm?.document_name || !requestForm?.document_type) return;
    setRequesting(true);
    await requestComplementaryDocument(dossierId, requestForm?.document_name, requestForm?.document_type);
    await fetchDocuments();
    setShowRequestModal(false);
    setRequestForm({ document_name: '', document_type: '' });
    setRequesting(false);
  };

  const handleChecklistToggle = async (doc, key) => {
    const currentChecklist = doc?.compliance_checklist || {};
    const updated = { ...currentChecklist, [key]: !currentChecklist?.[key] };
    await updateDocumentCompliance(doc?.id, updated);
    await fetchDocuments();
  };

  const handleDownload = async (doc) => {
    if (!doc?.file_path) return;
    setDownloadingId(doc?.id);
    const { url } = await getDocumentSignedUrl(doc?.file_path);
    setDownloadingId(null);
    if (url) window.open(url, '_blank');
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next?.has(docId)) next?.delete(docId);
      else next?.add(docId);
      return next;
    });
  };

  const selectAllPending = () => {
    const pendingIds = documents?.filter(d => d?.status === 'en_attente')?.map(d => d?.id);
    setSelectedDocs(new Set(pendingIds));
  };

  const handleBatchAction = async (action) => {
    if (selectedDocs?.size === 0) return;
    setBatchProcessing(true);
    const ids = Array.from(selectedDocs);
    await Promise.all(ids?.map(id => updateDocumentStatus(id, action)));
    const eventType = action === 'valide' ? 'document_approved' : 'document_rejected';
    const eventTitle = action === 'valide'
      ? `${ids?.length} document(s) approuvé(s) en lot`
      : `${ids?.length} document(s) rejeté(s) en lot`;
    await addTimelineEvent(dossierId, eventType, eventTitle, '', 'notaire');
    setSelectedDocs(new Set());
    setBatchMode(false);
    await fetchDocuments();
    onDocumentUpdated?.();
    setBatchProcessing(false);
  };

  const filteredDocuments = filterStatus === 'all'
    ? documents
    : documents?.filter(d => d?.status === filterStatus);

  const statusCounts = {
    all: documents?.length,
    en_attente: documents?.filter(d => d?.status === 'en_attente')?.length,
    valide: documents?.filter(d => d?.status === 'valide')?.length,
    rejete: documents?.filter(d => d?.status === 'rejete')?.length,
    complementaire_demande: documents?.filter(d => d?.status === 'complementaire_demande')?.length,
  };

  if (loading) {
    return (
      <div className="space-y-2.5">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="bg-slate-50 rounded-xl p-4 animate-pulse h-20" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center space-x-2">
          <div className="w-9 h-9 rounded-xl bg-brand-earth/10 flex items-center justify-center">
            <Icon name="FolderLock" size={17} className="text-brand-earth" />
          </div>
          <div>
            <h3 className="text-ios-callout font-system font-body-semibold text-text-primary">Coffre-fort documentaire</h3>
            <p className="text-ios-caption1 text-text-secondary">{documents?.length} document{documents?.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        {/* Action buttons — larger on mobile */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isNotaire && statusCounts?.en_attente > 0 && (
            <button onClick={() => { setBatchMode(v => !v); if (!batchMode) selectAllPending(); else setSelectedDocs(new Set()); }}
              className={`flex items-center space-x-1.5 px-3 py-2.5 rounded-xl text-ios-callout font-system font-body-semibold transition-colors border touch-manipulation ${
                batchMode ? 'bg-brand-earth text-white border-brand-earth' : 'bg-white border-border text-text-secondary hover:border-brand-gold/40 active:bg-slate-50'
              }`}>
              <Icon name="CheckSquare" size={15} />
              <span>Lot</span>
            </button>
          )}
          <button onClick={() => setShowUploadModal(true)}
            className="flex items-center space-x-1.5 px-3 py-2.5 bg-brand-gold text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-brand-gold/90 active:bg-brand-gold/80 transition-colors touch-manipulation">
            <Icon name="Upload" size={15} />
            <span>Déposer</span>
          </button>
          {isNotaire && (
            <button onClick={() => setShowRequestModal(true)}
              className="flex items-center space-x-1.5 px-3 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-brand-earth/90 active:bg-brand-earth/80 transition-colors touch-manipulation">
              <Icon name="Plus" size={15} />
              <span>Demander</span>
            </button>
          )}
        </div>
      </div>

      {/* Batch action bar */}
      {batchMode && selectedDocs?.size > 0 && (
        <div className="flex items-center justify-between p-4 bg-brand-earth/5 border border-brand-earth/20 rounded-xl flex-wrap gap-3">
          <p className="text-ios-callout font-system font-body-semibold text-brand-earth">
            {selectedDocs?.size} sélectionné{selectedDocs?.size > 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => handleBatchAction('valide')} disabled={batchProcessing}
              className="flex items-center space-x-1.5 px-4 py-2.5 bg-green-600 text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-green-700 active:bg-green-800 disabled:opacity-60 transition-colors touch-manipulation">
              {batchProcessing ? <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="CheckCircle2" size={15} />}
              <span>Approuver</span>
            </button>
            <button onClick={() => handleBatchAction('rejete')} disabled={batchProcessing}
              className="flex items-center space-x-1.5 px-4 py-2.5 bg-red-500 text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-red-600 active:bg-red-700 disabled:opacity-60 transition-colors touch-manipulation">
              <Icon name="XCircle" size={15} />
              <span>Rejeter</span>
            </button>
            <button onClick={() => { setSelectedDocs(new Set()); setBatchMode(false); }}
              className="w-10 h-10 rounded-xl hover:bg-slate-100 active:bg-slate-200 transition-colors flex items-center justify-center touch-manipulation">
              <Icon name="X" size={16} className="text-text-secondary" />
            </button>
          </div>
        </div>
      )}

      {/* Status filter tabs */}
      {documents?.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { key: 'all', label: 'Tous' },
            { key: 'en_attente', label: 'En attente' },
            { key: 'valide', label: 'Validés' },
            { key: 'rejete', label: 'Rejetés' },
            { key: 'complementaire_demande', label: 'Complémentaires' },
          ]?.map(tab => (
            <button key={tab?.key} onClick={() => setFilterStatus(tab?.key)}
              className={`flex-shrink-0 px-3 py-2 rounded-full text-[11px] font-system font-body-medium transition-all touch-manipulation ${
                filterStatus === tab?.key ? 'bg-brand-earth text-white' : 'bg-slate-100 text-text-secondary hover:bg-slate-200 active:bg-slate-300'
              }`}>
              {tab?.label}
              {statusCounts?.[tab?.key] > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${filterStatus === tab?.key ? 'bg-white/20 text-white' : 'bg-white text-text-secondary'}`}>
                  {statusCounts?.[tab?.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Documents */}
      {filteredDocuments?.length === 0 ? (
        <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-border">
          <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
            <Icon name="FileX" size={22} className="text-text-secondary" />
          </div>
          <p className="text-ios-callout font-system font-body-medium text-text-secondary">
            {filterStatus === 'all' ? 'Aucun document déposé' : 'Aucun document dans cette catégorie'}
          </p>
          <button onClick={() => setShowUploadModal(true)}
            className="mt-3 text-ios-callout font-system font-body-semibold text-brand-earth hover:underline py-2 px-4 touch-manipulation">
            Déposer le premier document →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDocuments?.map(doc => {
            const statusConf = DOC_STATUS_CONFIG?.[doc?.status] || DOC_STATUS_CONFIG?.en_attente;
            const typeConf = DOC_TYPE_ICONS?.[doc?.document_type] || DOC_TYPE_ICONS?.autre;
            const checklistItems = DOCUMENT_COMPLIANCE_CHECKLISTS?.[doc?.document_type] || [];
            const isExpanded = expandedDoc === doc?.id;
            const isAnnotationsOpen = expandedAnnotations === doc?.id;
            const isSelected = selectedDocs?.has(doc?.id);

            return (
              <div key={doc?.id} className={`bg-white rounded-2xl border overflow-hidden transition-all ${isSelected ? 'border-brand-earth ring-1 ring-brand-earth/20' : 'border-border/50'}`}>
                {/* Main row */}
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Batch checkbox — larger touch target */}
                    {batchMode && doc?.status === 'en_attente' && (
                      <button onClick={() => toggleDocSelection(doc?.id)}
                        className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all touch-manipulation ${isSelected ? 'bg-brand-earth border-brand-earth' : 'border-border hover:border-brand-gold active:border-brand-earth'}`}>
                        {isSelected && <Icon name="Check" size={12} color="white" strokeWidth={3} />}
                      </button>
                    )}
                    <div className="flex items-start space-x-3 min-w-0 flex-1">
                      <div className={`w-10 h-10 rounded-xl ${typeConf?.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Icon name={typeConf?.icon} size={17} className={typeConf?.color} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-ios-callout font-system font-body-semibold text-text-primary truncate">{doc?.document_name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <p className="text-ios-caption1 text-text-secondary">{doc?.document_type?.replace(/_/g, ' ')}</p>
                          {doc?.uploaded_by_role && (
                            <span className="text-[10px] font-system px-1.5 py-0.5 bg-slate-100 text-text-secondary rounded-full">
                              {UPLOADER_ROLE_LABELS?.[doc?.uploaded_by_role] || doc?.uploaded_by_role}
                            </span>
                          )}
                          {doc?.file_size && (
                            <span className="text-[10px] text-text-secondary font-system">{(doc?.file_size / 1024 / 1024)?.toFixed(1)} Mo</span>
                          )}
                        </div>
                        {doc?.compliance_score !== null && doc?.compliance_score !== undefined && checklistItems?.length > 0 && (
                          <ComplianceBar score={doc?.compliance_score} />
                        )}
                        {doc?.notary_notes && (
                          <p className="text-ios-caption1 text-text-secondary mt-1 italic">Note: {doc?.notary_notes}</p>
                        )}
                      </div>
                    </div>
                    {/* Status badge */}
                    <span className={`text-[11px] font-system font-body-medium px-2 py-1 rounded-full border flex items-center gap-1 flex-shrink-0 ${statusConf?.bg} ${statusConf?.text} ${statusConf?.border}`}>
                      <Icon name={statusConf?.icon} size={10} />
                      <span className="hidden sm:inline">{statusConf?.label}</span>
                    </span>
                  </div>

                  {/* Action row — full width on mobile for easy tapping */}
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {doc?.file_path && (
                      <button onClick={() => handleDownload(doc)} disabled={downloadingId === doc?.id}
                        className="flex items-center space-x-1.5 px-3 py-2 rounded-xl bg-slate-50 border border-border hover:bg-brand-gold/10 hover:border-brand-gold/30 active:bg-brand-gold/20 transition-colors touch-manipulation text-[11px] font-system text-text-secondary">
                        {downloadingId === doc?.id
                          ? <div className="w-3.5 h-3.5 border border-brand-earth border-t-transparent rounded-full animate-spin" />
                          : <Icon name="Download" size={13} className="text-text-secondary" />}
                        <span>Télécharger</span>
                      </button>
                    )}
                    {checklistItems?.length > 0 && (
                      <button onClick={() => setExpandedDoc(isExpanded ? null : doc?.id)}
                        className={`flex items-center space-x-1.5 px-3 py-2 rounded-xl border transition-colors touch-manipulation text-[11px] font-system ${isExpanded ? 'bg-brand-gold/10 border-brand-gold/30 text-brand-earth' : 'bg-slate-50 border-border text-text-secondary hover:bg-brand-gold/10 hover:border-brand-gold/30'}`}>
                        <Icon name="ClipboardList" size={13} />
                        <span>Conformité</span>
                      </button>
                    )}
                    {isNotaire && (
                      <button onClick={() => setExpandedAnnotations(isAnnotationsOpen ? null : doc?.id)}
                        className={`flex items-center space-x-1.5 px-3 py-2 rounded-xl border transition-colors touch-manipulation text-[11px] font-system ${isAnnotationsOpen ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-border text-text-secondary hover:bg-blue-50 hover:border-blue-200'}`}>
                        <Icon name="MessageSquare" size={13} />
                        <span>Annoter</span>
                      </button>
                    )}
                    {/* Approve/Reject — prominent on mobile */}
                    {isNotaire && doc?.status === 'en_attente' && !batchMode && (
                      <>
                        <button onClick={() => handleUpdateStatus(doc?.id, 'valide')} disabled={updatingId === doc?.id}
                          className="flex items-center space-x-1.5 px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-xl text-[11px] font-system font-body-semibold hover:bg-green-100 active:bg-green-200 transition-colors disabled:opacity-50 touch-manipulation">
                          <Icon name="CheckCircle2" size={13} />
                          <span>{updatingId === doc?.id ? '...' : 'Valider'}</span>
                        </button>
                        <button onClick={() => { setRejectModal(doc?.id); setRejectReason(''); }} disabled={updatingId === doc?.id}
                          className="flex items-center space-x-1.5 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-xl text-[11px] font-system font-body-semibold hover:bg-red-100 active:bg-red-200 transition-colors disabled:opacity-50 touch-manipulation">
                          <Icon name="XCircle" size={13} />
                          <span>Rejeter</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Compliance Checklist — field-optimized with large checkboxes */}
                {isExpanded && checklistItems?.length > 0 && (
                  <div className="border-t border-border/50 bg-slate-50 px-4 py-4">
                    <p className="text-[11px] font-system font-body-semibold text-text-secondary uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Icon name="ClipboardCheck" size={12} />
                      Checklist de conformité
                    </p>
                    <div className="space-y-3">
                      {checklistItems?.map(item => {
                        const checked = doc?.compliance_checklist?.[item?.key] || false;
                        return (
                          <button key={item?.key}
                            onClick={() => isNotaire && handleChecklistToggle(doc, item?.key)}
                            disabled={!isNotaire}
                            className={`w-full flex items-start space-x-3 text-left transition-opacity touch-manipulation ${isNotaire ? 'active:opacity-70' : 'cursor-default'}`}>
                            <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                              checked ? 'bg-brand-earth border-brand-earth' : isNotaire ? 'border-border hover:border-brand-gold' : 'border-border'
                            }`}>
                              {checked && <Icon name="Check" size={13} color="white" strokeWidth={3} />}
                            </div>
                            <span className={`text-ios-callout font-system leading-relaxed ${checked ? 'text-text-secondary line-through' : 'text-text-primary'}`}>{item?.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Annotations panel */}
                {isAnnotationsOpen && isNotaire && (
                  <AnnotationsPanel doc={doc} notaireProfile={notaireProfile} onAnnotationAdded={() => onDocumentUpdated?.()} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <DocumentUploadModal dossierId={dossierId} onClose={() => setShowUploadModal(false)}
          onUploaded={async () => { await fetchDocuments(); onDocumentUpdated?.(); }} />
      )}

      {/* Request Complementary Modal — bottom sheet on mobile */}
      {showRequestModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 bg-slate-200 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
              <h3 className="text-ios-headline font-display font-display-semibold text-text-primary">Demander une pièce</h3>
              <button onClick={() => setShowRequestModal(false)} className="w-9 h-9 rounded-xl hover:bg-slate-100 active:bg-slate-200 transition-colors flex items-center justify-center touch-manipulation">
                <Icon name="X" size={18} className="text-text-secondary" />
              </button>
            </div>
            <form onSubmit={handleRequestDoc} className="p-5 space-y-4">
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">Nom du document *</label>
                <input type="text" value={requestForm?.document_name} onChange={e => setRequestForm(f => ({ ...f, document_name: e?.target?.value }))}
                  placeholder="Ex: Certificat de propriété"
                  className="w-full px-4 py-3.5 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all" />
              </div>
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">Type de document *</label>
                <select value={requestForm?.document_type} onChange={e => setRequestForm(f => ({ ...f, document_type: e?.target?.value }))}
                  className="w-full px-4 py-3.5 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all bg-white">
                  <option value="">Sélectionner...</option>
                  <option value="titre_propriete">Titre de propriété</option>
                  <option value="identite">Pièce d'identité</option>
                  <option value="plan">Plan cadastral</option>
                  <option value="acte">Acte notarié</option>
                  <option value="fiscal">Document fiscal</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2 pb-safe">
                <button type="button" onClick={() => setShowRequestModal(false)}
                  className="flex-1 py-3.5 border border-border rounded-xl text-ios-callout font-system font-body-medium text-text-secondary hover:bg-slate-50 active:bg-slate-100 transition-colors touch-manipulation">Annuler</button>
                <button type="submit" disabled={requesting}
                  className="flex-1 py-3.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-brand-earth/90 active:bg-brand-earth/80 disabled:opacity-60 transition-colors touch-manipulation">
                  {requesting ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal — bottom sheet on mobile */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-sm max-h-[90vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 bg-slate-200 rounded-full" />
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <Icon name="XCircle" size={18} className="text-red-500" />
                </div>
                <h3 className="text-ios-headline font-display font-display-semibold text-text-primary">Rejeter le document</h3>
              </div>
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">Motif du rejet (optionnel)</label>
                <textarea value={rejectReason} onChange={e => setRejectReason(e?.target?.value)}
                  placeholder="Ex: Document illisible, date expirée..." rows={3}
                  className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-red-300 focus:border-transparent transition-all resize-none" />
              </div>
              <div className="flex gap-3 pb-safe">
                <button onClick={() => { setRejectModal(null); setRejectReason(''); }}
                  className="flex-1 py-3.5 border border-border rounded-xl text-ios-callout font-system font-body-medium text-text-secondary hover:bg-slate-50 active:bg-slate-100 transition-colors touch-manipulation">Annuler</button>
                <button onClick={handleReject} disabled={updatingId === rejectModal}
                  className="flex-1 py-3.5 bg-red-500 text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-red-600 active:bg-red-700 disabled:opacity-60 transition-colors touch-manipulation">
                  {updatingId === rejectModal ? 'Rejet...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentVault;
